import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DebtCard from "@/components/DebtCard";
import { useAuth } from "@/context/AuthContext";
import { fmtMoneyList } from "@/lib/format";
import api from "@/lib/api";

export default function PersonHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const isPlus = user?.subscription_status === "plus";
  const { data, isLoading } = useQuery({
    queryKey: ["person", id],
    queryFn: async () => (await api.get(`/people/${id}`)).data,
  });

  const downloadStatement = async () => {
    if (!isPlus) {
      toast("PDF statements are a Debii Plus feature");
      navigate("/upgrade");
      return;
    }
    setDownloading(true);
    try {
      const res = await api.get(`/people/${id}/statement`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `debii-statement-${(data?.name || "person").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error("Couldn't generate the statement. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="px-1 space-y-4">
        <Skeleton className="h-8 w-24 rounded-lg" />
        <Skeleton className="h-32 w-full rounded-3xl" />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <button data-testid="person-back" onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
        <ArrowLeft className="h-5 w-5 text-primary" />
      </button>

      <div className="mt-4 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground">{data.name}</h1>
        {data.owed_to_me?.count > 0 && (
          <>
            <p className="mt-3 text-sm text-muted-foreground">Currently owes you</p>
            <p data-testid="person-outstanding" className="font-display text-4xl font-extrabold tabular text-foreground">
              {fmtMoneyList(data.owed_to_me.outstanding_by_currency)}
            </p>
          </>
        )}
        {data.i_owe?.count > 0 && (
          <div className={data.owed_to_me?.count > 0 ? "mt-3" : "mt-3"}>
            <p className="text-sm text-muted-foreground">You owe them</p>
            <p data-testid="person-i-owe" className="font-display text-3xl font-extrabold tabular text-foreground/80">
              {fmtMoneyList(data.i_owe.outstanding_by_currency)}
            </p>
          </div>
        )}
        {data.owed_to_me?.count === 0 && data.i_owe?.count === 0 && (
          <p className="mt-3 text-sm text-emerald-700 font-medium">All settled ✓</p>
        )}
      </div>

      {data.owed_to_me?.count > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-2 text-center">
          <Stat label="Total lent" value={fmtMoneyList(data.total_lent_by_currency)} />
          <Stat label="Total repaid" value={fmtMoneyList(data.total_repaid_by_currency)} />
          <Stat label="Outstanding" value={fmtMoneyList(data.outstanding_by_currency)} />
        </div>
      )}

      <h2 className="mt-8 mb-3 text-sm font-bold uppercase tracking-wide text-foreground">History</h2>
      <div className="space-y-2.5">
        {data.debts.map((d) => <DebtCard key={d.id} debt={d} />)}
      </div>

      <Button
        data-testid="person-statement-button"
        variant="outline"
        onClick={downloadStatement}
        disabled={downloading}
        className="mt-6 w-full h-12 rounded-xl gap-2"
      >
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" />
          : isPlus ? <FileText className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        {downloading ? "Preparing…" : "Download PDF statement"}
        {!isPlus && <span className="ml-1 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-bold text-accent">PLUS</span>}
      </Button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3">
      <p className="font-display font-bold text-foreground tabular text-sm">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
